import React, { useEffect, useRef } from 'react';
import { Controller, useFormContext, FieldError } from 'react-hook-form';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import {
  AddAccountSelectionModalWrapper,
  Selection,
} from '../../../../../../Profile/Accounts/components/AddAccountSelectionModalWrapper/AddAccountSelectionModalWrapper';
import { AccountModalUsageContext } from '../../../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';
import {
  AccountDTO,
  useDafAccountsQuery,
} from '../../../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/useDafAccountsQuery';
import { CharitableGoalDataDTO } from '../../CharitableGiving.meta';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import { Shimmer } from '../../../../../../CommonComponents/OPL/Shimmer/Shimmer';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';

interface Props {
  goalId: string | null;
}

export const DafAccount = ({ goalId }: Props) => {
  const { isLoading, data } = useDafAccountsQuery(goalId);

  if (isLoading) {
    return (
      <Box
        display="grid"
        alignItems="end"
        css={`
          grid-template-columns: 1fr 150px;
          gap: 24px 16px;
        `}
      >
        <Box>
          <Text variant="formLabel">Select DAF account</Text>

          <Shimmer width="100%" height="37px" mt={4} />
        </Box>

        <Button variant="subtleActive" leftIcon="fa fa-plus" onClick={() => {}} disabled>
          Add DAF account
        </Button>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return <DafAccountContent dafAccounts={data} />;
};

interface DafAccountContentProps {
  dafAccounts: AccountDTO[];
}

const DafAccountContent = ({ dafAccounts }: DafAccountContentProps) => {
  const [isOpenAddAccountModal, setIsOpenAddAccountModal] = React.useState(false);

  const { control, setValue, formState } = useFormContext<CharitableGoalDataDTO>();

  /**
   * Effect to allow auto-selecting the added account
   */
  const dafAccountsRef = useRef(dafAccounts.map(({ id }) => id));

  useEffect(() => {
    if (dafAccounts.length !== dafAccountsRef.current.length) {
      const newDafAccount =
        dafAccounts.find(({ id }) => !dafAccountsRef.current.includes(id))?.id || null;

      setValue('dafAccount', newDafAccount);

      dafAccountsRef.current = dafAccounts.map(({ id }) => id);
    }
  }, [dafAccounts, setValue]);

  const accountOptions = dafAccounts.map(({ id, name, institution, balance }) => ({
    value: id,
    label: name,
    subLabel: `${institution ?? ''} ${balance ? `- ${formatCurrency(balance)}` : ''}`,
  }));

  return (
    <Box
      display="grid"
      alignItems="end"
      css={`
        grid-template-columns: 1fr 150px;
        gap: 24px 16px;
      `}
    >
      <Controller
        control={control}
        name="dafAccount"
        render={({ field: { value, onChange, ...field } }) => {
          return (
            <Select
              {...field}
              isPortal
              label="Select DAF account"
              value={accountOptions.find(opt => opt.value === value)}
              options={accountOptions}
              onChange={opt => {
                if (opt) {
                  onChange(opt.value);
                }
              }}
              formVariant={formState.errors.dafAccount ? 'error' : undefined}
              helperText={(formState.errors.dafAccount as FieldError)?.message}
            />
          );
        }}
      />

      <Button
        variant="subtleActive"
        leftIcon="fa fa-plus"
        onClick={() => {
          setIsOpenAddAccountModal(true);
        }}
      >
        Add DAF account
      </Button>
      {isOpenAddAccountModal && (
        <AddAccountSelectionModalWrapper
          initialSelection={Selection.Manual}
          usageContext={AccountModalUsageContext.DafAccounts}
          showExternalProviders={false}
          onClose={() => setIsOpenAddAccountModal(false)}
        />
      )}
    </Box>
  );
};
